import { allEnginesDone as allEnginesDoneFn } from '@modules/controladoria-v2/utils/allEnginesDone'
import {
  mesAnoFromDataReferencia,
  formatDataReferenciaDisplay,
} from '@modules/controladoria-v2/utils/monthFormat'
import {
  ENGINE_STATUS,
  filterEnginesByAccess,
} from '@modules/controladoria-v2/constants/Status'
import { DEFAULT_TAX_PARAMS } from '@modules/controladoria-v2/constants/taxParams'

// ---------- Fechamentos ----------

export const fechamentos = (state) => {
  if (state && state.fechamentos && Array.isArray(state.fechamentos.data)) {
    return state.fechamentos.data
  }
  return []
}

// Returns the raw `fechamentoAtual` shape (`{ dataReferencia }`). The single canonical
// field. All consumers should prefer the derived helpers below (`dataReferencia`,
// `mesAtual`, `anoAtual`, `mesNomeAtual`, `mesAnoDisplay`).
export const fechamentoAtual = (state) => {
  if (state && state.fechamentoAtual) return state.fechamentoAtual
  return { dataReferencia: null }
}

// The `DD/MM/YYYY` data_referencia string — sent to the backend by the API layer.
export const dataReferencia = (state) => {
  if (state && state.fechamentoAtual && state.fechamentoAtual.dataReferencia) {
    return state.fechamentoAtual.dataReferencia
  }
  return null
}

// Derived getters for legacy display logic (e.g. route encoding, month-name labels).
// Backed off the canonical `dataReferencia` so they stay in sync automatically.

export const mesAtual = (state) => {
  const parts = mesAnoFromDataReferencia(dataReferencia(state))
  return parts ? parts.monthIndex + 1 : null
}

export const anoAtual = (state) => {
  const parts = mesAnoFromDataReferencia(dataReferencia(state))
  return parts ? parts.ano : null
}

export const mesNomeAtual = (state) => {
  const parts = mesAnoFromDataReferencia(dataReferencia(state))
  return parts ? parts.mes : ''
}

export const mesAnoDisplay = (state) =>
  formatDataReferenciaDisplay(dataReferencia(state))

// ---------- Importação ----------

export const importacoes = (state) => {
  if (state && state.importacoes) return state.importacoes
  return {}
}

export const importacoesDataReferencia = (state) => {
  if (state && typeof state.importacoesDataReferencia === 'string') {
    return state.importacoesDataReferencia
  }
  return null
}

export const importacao = (state) => (key) => {
  if (!key || !state || !state.importacoes) return null
  return state.importacoes[key] || null
}

// Mapa `{ tipo: { id, status, message, data_atualizacao } }` do processamento no
// dataroom (lido do slice padrão `{ data, loading, lastUpdate }`). Retorna `{}`
// por default (nunca undefined).
export const dataroomStatus = (state) => {
  if (state && state.dataroomStatus && state.dataroomStatus.data) {
    return state.dataroomStatus.data
  }
  return {}
}

// ---------- ConfigDRE ----------

export const configDRE = (state) => {
  if (state && state.configDRE) return state.configDRE
  return {}
}

export const configGrid = (state) => (tabKey) => {
  if (!tabKey || !state || !state.configDRE) return []
  const rows = state.configDRE[tabKey]
  return Array.isArray(rows) ? rows : []
}

// Dicionário `{ campo: [valores] }` retornado pelo endpoint
// /configuracao-dre/dropdown-options. Carregado por fechamento pela
// view-configuracao-dre (as opções de rateio incluem as listas do mês).
export const configDREDropdownOptions = (state) => {
  if (state && state.configDREDropdownOptions) {
    return state.configDREDropdownOptions
  }
  return {}
}

// `data_referencia` (DD/MM/YYYY) a que o cache de opções pertence — usado pela
// API para decidir entre cache e refetch ao trocar de fechamento.
export const configDREDropdownOptionsDataReferencia = (state) => {
  if (
    state &&
    typeof state.configDREDropdownOptionsDataReferencia === 'string'
  ) {
    return state.configDREDropdownOptionsDataReferencia
  }
  return null
}

// ---------- Listas ----------

export const listas = (state) => {
  if (state && state.listas && Array.isArray(state.listas.data)) {
    return state.listas.data
  }
  return []
}

export const listasLoading = (state) => {
  if (state && state.listas) return Boolean(state.listas.loading)
  return false
}

export const listasDataReferencia = (state) => {
  if (state && state.listas && typeof state.listas.dataReferencia === 'string') {
    return state.listas.dataReferencia
  }
  return null
}

// ---------- Engines ----------

// Mapa `{ engineId: { status, ... } }` do fechamento carregado. Continua sendo
// o único ponto de acesso dos consumidores — quem precisa saber se ele já vale
// para o mês selecionado usa `engineStatusReady`.
export const engineStatus = (state) => {
  if (state && state.engineStatus && state.engineStatus.data) {
    return state.engineStatus.data
  }
  return {}
}

export const engineStatusLoading = (state) => {
  if (state && state.engineStatus) return Boolean(state.engineStatus.loading)
  return false
}

export const engineStatusError = (state) => {
  if (state && state.engineStatus && state.engineStatus.error) {
    return state.engineStatus.error
  }
  return ''
}

// A `data_referencia` que gerou o mapa em memória — null enquanto nunca
// carregou ou logo após trocar de fechamento (SET_FECHAMENTO_ATUAL invalida).
export const engineStatusDataReferencia = (state) => {
  if (
    state
    && state.engineStatus
    && typeof state.engineStatus.dataReferencia === 'string'
  ) {
    return state.engineStatus.dataReferencia
  }
  return null
}

// True quando o status em memória pertence ao fechamento selecionado agora.
// É o gate do skeleton: `false` significa "ainda não sei o status deste mês",
// seja porque nunca carregou, seja porque o usuário acabou de trocar de mês.
// Não depende de `loading` de propósito — durante o polling do mesmo mês os
// dados continuam válidos e as pills não devem piscar a cada 5s.
export const engineStatusReady = (state) => {
  const atual = dataReferencia(state)
  if (!atual) return false
  return engineStatusDataReferencia(state) === atual
}

export const totalEnginesRunning = (state) => {
  const map = engineStatus(state)
  return Object.values(map).filter(
    (entry) => entry && entry.status === 'rodando'
  ).length
}

// Só considera os motores que o escritório efetivamente tem acesso (feature
// flags). Um escritório que só roda DRE Geral + DFC não fica travado pelos
// motores de Profissional/Filial/Equipe ou Área de Produto, que ele nem
// consegue rodar. As flags vêm de `parameters/show` (o mesmo objeto que `$access`).
export const allEnginesDone = (state, _getters, _rootState, rootGetters) => {
  if (!state) return false
  const access = (rootGetters && rootGetters['parameters/show']) || {}
  const accessibleEngineIds = filterEnginesByAccess(access).map(
    (engine) => engine.id,
  )
  return allEnginesDoneFn(engineStatus(state), accessibleEngineIds)
}

// Indica que existe ao menos um motor com resultado concluído (sucesso ou
// avisos) cuja versão ainda não foi publicada. Quando o usuário fecha a
// versão, os motores migram para `versao_fechada` e o aviso some.
export const hasUnpublishedEngineSuccess = (state) => {
  if (!state) return false
  return Object.values(engineStatus(state)).some(
    (entry) =>
      entry
      && (entry.status === ENGINE_STATUS.SUCESSO
        || entry.status === ENGINE_STATUS.AVISOS),
  )
}

// ---------- Parâmetros de impostos ----------

export const taxParams = (state) => {
  if (state && state.taxParams && typeof state.taxParams === 'object') {
    return state.taxParams
  }
  return { ...DEFAULT_TAX_PARAMS }
}

// ---------- Versão ----------

export const versoesPublicadas = (state) => {
  if (state && typeof state.versoesPublicadas === 'number') {
    return state.versoesPublicadas
  }
  return 0
}
